import { Modal, ModalOverflow, ModalDialog, ModalClose, Typography, Box, Stack, Chip, Divider, Alert, CircularProgress, Card, Button, Textarea, FormControl, FormLabel } from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useContext } from "react";
import { LayoutContext } from "../../context";
import { APIGetTestResultDetail, APIUpdateTestResult } from "../../api";
import { CheckCircleRounded, CancelRounded, QuestionMarkRounded, CommentRounded, SaveRounded } from "@mui/icons-material";



export default function TestResultModal() {
  const { token, currentDoctor, modalResults, setModalResults } = useContext(LayoutContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [doctorNotes, setDoctorNotes] = useState("");

  // Загрузка деталей результата
  useEffect(() => {
    if (modalResults?.open && modalResults?.resultId) {
      loadResultDetails();
    }
  }, [modalResults]);

  const loadResultDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await APIGetTestResultDetail(token, modalResults?.resultId);
      
      if (response.success) {
        setResult(response.result);
        setDoctorNotes(response.result.doctor_notes || "");
      } else {
        setError(response.error || "Ошибка при загрузке результатов");
      }
    } catch (error) {
      console.error("Ошибка при загрузке деталей результата:", error);
      setError(error?.response?.data?.error || "Ошибка при загрузке деталей результата");
    } finally {
      setLoading(false);
    }
  };

  // Сохранение комментариев врача
  const handleSaveNotes = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await APIUpdateTestResult(token, modalResults?.resultId, {
        doctor_notes: doctorNotes,
      });
      
      if (response.success) {
        setResult(response.result);
        if (onResultUpdated) onResultUpdated();
        alert("Комментарий успешно сохранен");
      } else {
        setError(response.error || "Ошибка при сохранении комментария");
      }
    } catch (error) {
      console.error("Ошибка при сохранении комментария:", error);
      setError("Ошибка при сохранении комментария");
    } finally {
      setSaving(false);
    }
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Получение цвета для статуса ответа
  const getAnswerColor = (isCorrect) => {
    return isCorrect ? "success" : "danger";
  };

  // Получение иконки для статуса ответа
  const getAnswerIcon = (isCorrect) => {
    return isCorrect ? <CheckCircleRounded /> : <CancelRounded />;
  };

  // Проверяем, является ли пользователь врачом
  const isDoctor = currentDoctor?.type_profile.includes("doctor") || currentDoctor?.type_profile?.includes("doctor");

  return (
    <AnimatePresence>
      {modalResults?.open && (
        <Modal
          sx={{ zIndex: 10010 }}
          open={modalResults?.open}
          slotProps={{
            backdrop: {
              component: motion.div,
              initial: { opacity: 0, backdropFilter: "blur(0px)" },
              animate: { opacity: 1, backdropFilter: "blur(8px)" },
              exit: { opacity: 0, backdropFilter: "blur(0px)" },
              transition: { duration: 0.3, ease: "easeInOut" },
            },
          }}
        >
          <ModalOverflow sx={{ height: "auto", maxHeight: "90vh" }}>
            <ModalDialog
              size="lg"
              component={motion.div}
              initial={{ opacity: 0, y: -10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              sx={(theme) => ({
                [theme.breakpoints.up("xl")]: { width: "800px", mx: 0 },
                [theme.breakpoints.down("xl")]: { width: "700px", mx: 0 },
                [theme.breakpoints.down("lg")]: { width: "600px", mx: 0 },
                [theme.breakpoints.down("md")]: { width: "95%", p: 3 },
                width: "100%",
                overflow: "auto",
              })}
            >
              <ModalClose onClick={() => setModalResults({ open: false, resultId: null })} />

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert color="danger" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              ) : result ? (
                <>
                  {/* Заголовок */}
                  <Typography level="h3" sx={{ mb: 2, textAlign: "center" }}>
                    Результат тестирования
                  </Typography>

                  {/* Блок с общей информацией */}
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <Typography level="h4" sx={{ mb: 2 }}>
                      Общая информация
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Stack spacing={2}>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography fontWeight="lg">Пациент:</Typography>
                        <Typography>{result.user_info?.name || "Не указано"}</Typography>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography fontWeight="lg">Тест:</Typography>
                        <Typography>{result.test_info?.name || "Тест"}</Typography>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography fontWeight="lg">Дата прохождения:</Typography>
                        <Typography>{formatDate(result.date_create)}</Typography>
                      </Box>

                      {result.doctor_info && (
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography fontWeight="lg">Врач:</Typography>
                          <Typography>{result.doctor_info.name}</Typography>
                        </Box>
                      )}

                      {/* Плашки с результатами */}
                      {result.score !== null && result.score !== undefined && (
                        <Box sx={{ mt: 2 }}>
                          <Typography level="title-sm" sx={{ mb: 1 }}>
                            Общий результат:
                          </Typography>
                          <Chip
                            size="lg"
                            variant="soft"
                            color={
                              result.score > 70
                                ? "success"
                                : result.score > 40
                                ? "warning"
                                : "danger"
                            }
                          >
                            {result.score.toFixed(1)}%
                          </Chip>
                        </Box>
                      )}
                    </Stack>
                  </Card>

                  {/* Блок с ответами на вопросы */}
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <Typography level="h4" sx={{ mb: 2 }}>
                      Ответы на вопросы
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {result.user_answers && result.user_answers.length > 0 ? (
                      <Stack spacing={3}>
                        {result.user_answers.map((userAnswer, index) => (
                          <Box key={userAnswer.id}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                              <QuestionMarkRounded sx={{ mr: 1, color: "neutral.500" }} />
                              <Typography level="title-md">
                                Вопрос {index + 1}: {userAnswer.question?.text}
                              </Typography>
                            </Box>

                            {/* Информация об ответе */}
                            <Card variant="soft" sx={{ mt: 1 }}>
                              <Stack spacing={1}>
                                {userAnswer.question?.question_type === "single_choice" && (
                                  <>
                                    <Typography level="body-sm">Выбранный вариант:</Typography>
                                    {userAnswer.selected_options_info?.map((option) => (
                                      <Box
                                        key={option.id}
                                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                      >
                                        <Chip
                                          size="sm"
                                          variant="soft"
                                          color={getAnswerColor(option.is_correct)}
                                          startDecorator={getAnswerIcon(option.is_correct)}
                                        >
                                          {option.text}
                                        </Chip>
                                        <Typography level="body-xs" color="neutral">
                                          ({option.points} баллов)
                                        </Typography>
                                      </Box>
                                    ))}
                                  </>
                                )}

                                {userAnswer.question?.question_type === "multiple_choice" && (
                                  <>
                                    <Typography level="body-sm">Выбранные варианты:</Typography>
                                    <Stack spacing={0.5}>
                                      {userAnswer.selected_options_info?.map((option) => (
                                        <Box
                                          key={option.id}
                                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                        >
                                          <Chip
                                            size="sm"
                                            variant="soft"
                                            color={getAnswerColor(option.is_correct)}
                                            startDecorator={getAnswerIcon(option.is_correct)}
                                          >
                                            {option.text}
                                          </Chip>
                                          <Typography level="body-xs" color="neutral">
                                            ({option.points} баллов)
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Stack>
                                  </>
                                )}

                                {userAnswer.question?.question_type === "text" && (
                                  <>
                                    <Typography level="body-sm">Текстовый ответ:</Typography>
                                    <Typography level="body-sm" sx={{ fontStyle: "italic" }}>
                                      "<b>{userAnswer.text_answer}</b>"
                                    </Typography>
                                  </>
                                )}

                                {userAnswer.question?.question_type === "scale" && (
                                  <>
                                    <Typography level="body-sm">Ответ по шкале:</Typography>
                                    <Typography level="body-sm">
                                      {userAnswer.scale_answer} / 10
                                    </Typography>
                                  </>
                                )}

                                {userAnswer.question?.question_type === "yes_no" && (
                                  <>
                                    <Typography level="body-sm">Ответ:</Typography>
                                    <Typography level="body-sm">
                                      {userAnswer.numeric_answer === 1 ? "Да" : "Нет"}
                                    </Typography>
                                  </>
                                )}

                                {userAnswer.question?.question_type === "likert" && (
                                  <>
                                    <Typography level="body-sm">Ответ по шкале Лайкерта:</Typography>
                                    <Typography level="body-sm">
                                      {userAnswer.numeric_answer}
                                    </Typography>
                                  </>
                                )}

                                {/* Статус правильности ответа */}
                                {(userAnswer.is_correct !== undefined &&
                                  userAnswer.question?.question_type !== "text" && userAnswer.question?.question_type !== "multiple_choice"
                                ) && (
                                  <Box sx={{ mt: 1 }}>
                                    <Chip
                                      size="sm"
                                      variant="outlined"
                                      color={getAnswerColor(userAnswer.is_correct)}
                                      startDecorator={getAnswerIcon(userAnswer.is_correct)}
                                    >
                                      {userAnswer.is_correct ? "Правильно" : "Неправильно"}
                                    </Chip>
                                  </Box>
                                )}
                              </Stack>
                            </Card>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Alert color="neutral">Ответы на вопросы не найдены</Alert>
                    )}
                  </Card>

                  {/* Блок для комментариев врача */}
                  {isDoctor && (
                    <Card variant="outlined">
                      <Typography level="h4" color="#1E6E35" sx={{ mb: 2 }} startDecorator={<CommentRounded/>}>
                        Комментарий врача
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <FormControl>
                        <FormLabel>Заметки и рекомендации</FormLabel>
                        <Textarea
                          minRows={4}
                          placeholder="Введите ваши комментарии, рекомендации или наблюдения..."
                          value={doctorNotes}
                          onChange={(e) => setDoctorNotes(e.target.value)}
                          sx={{ mb: 2 }}
                        />
                      </FormControl>

                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                        <Button
                          variant="outlined"
                          color="neutral"
                          onClick={() => setModalResults({ open: false, resultId: null })}
                          disabled={saving}
                        >
                          Закрыть
                        </Button>
                        <Button
                          variant="solid"
                          color="primary"
                          startDecorator={<SaveRounded />}
                          loading={saving}
                          onClick={handleSaveNotes}
                          disabled={saving}
                        >
                          Сохранить комментарий
                        </Button>
                      </Box>
                    </Card>
                  )}

                  {/* Кнопка закрытия для не-врачей */}
                  {!isDoctor && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                      <Button variant="outlined" color="neutral" onClick={() => setModalResults({ open: false, resultId: null })}>
                        Закрыть
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Alert color="neutral">Данные результата не найдены</Alert>
              )}
            </ModalDialog>
          </ModalOverflow>
        </Modal>
      )}
    </AnimatePresence>
  );
}